import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NavIcon from './NavIcon.jsx';

/**
 * The workbench sidebar: renders lib/navigation.js navigationFor() groups.
 * The active item comes from the route path only (activeId from locate()),
 * so exactly one item is current.
 *
 * variant 'rail' (the fixed sidebar): full labels from 1024 px; from 768 to
 * 1023 px icons only, with the label as the accessible name and a floating
 * label on hover and focus. variant 'drawer' (below 768 px): full labels.
 *
 * The floating label meets WCAG 2.2 1.4.13: hoverable (no pointer-events-none,
 * the pointer can move onto it), persistent while hovered or focused, and
 * dismissible with Escape without moving focus or the pointer, wherever focus
 * is (a mouse user hovering a rail item has focus in the page, not in the nav).
 * Hovering or focusing a link shows the tooltips again. The Escape listener
 * never calls preventDefault, so the drawer's focus trap keeps working.
 *
 * `badges` maps an item id to { text, label }: a count in a text pill after the
 * label (ENTRAID-347 Activity badge). The pill is decoration; the link's
 * accessible name says it in words ("Activity, 2 unanswered critical").
 */
// The floating label's own layer: above every page layer (sticky bars and cards use up to z-20) and the shell popovers
// (z-40), below the navigation drawer (z-50) and dialogs (z-[60]). Pinned by Sidebar.layer.test.jsx (2026-10-30: it
// rendered behind the Data reference contents bar, which shared z-20 and came later in the page).
export const NAV_TOOLTIP_LAYER = 'z-[45]';

export default function Sidebar({ groups, activeId, variant = 'rail', onNavigate, badges = {} }) {
  const rail = variant === 'rail';
  const [tipsDismissed, setTipsDismissed] = useState(false);
  const showTips = () => setTipsDismissed(false);

  useEffect(() => {
    if (!rail) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape') setTipsDismissed(true);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [rail]);

  return (
    <nav aria-label="Sections" className="py-5">
      {groups.map((group) => {
        const headingId = `nav-group-${variant}-${group.id}`;
        return (
          <div key={group.id} role="group" aria-labelledby={headingId} className="mb-5">
            <p id={headingId} className={`mb-1.5 px-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-ipc-muted ${rail ? 'md:max-lg:sr-only' : ''}`}>
              {group.label}
            </p>
            <ul className="space-y-0.5 px-3">
              {group.items.map((item) => {
                const current = item.id === activeId;
                const badge = badges[item.id];
                return (
                  <li key={item.id}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      onMouseEnter={showTips}
                      onFocus={showTips}
                      aria-current={current ? 'page' : undefined}
                      aria-label={badge ? `${item.label}, ${badge.label}` : undefined}
                      className={`group relative flex items-center gap-3 rounded-lg px-3 py-[9px] text-sm ${
                        current
                          ? 'bg-ipc-tint font-semibold text-ipc-primary shadow-[inset_3px_0_0_var(--sky)]'
                          : 'text-ipc-ink hover:bg-ipc-surface'
                      } ${rail ? 'md:max-lg:justify-center' : ''}`}
                    >
                      <NavIcon id={item.id} className={`h-[18px] w-[18px] shrink-0 ${current ? 'text-ipc-light' : 'text-[#7A8BA3]'}`} />
                      <span data-nav-label className={rail ? 'md:max-lg:sr-only' : ''}>{item.label}</span>
                      {badge ? (
                        <span
                          aria-hidden="true"
                          data-nav-badge=""
                          className={`ml-auto rounded-sm bg-ipc-primary px-1.5 py-px text-[11px] font-semibold leading-4 text-white ${rail ? 'md:max-lg:absolute md:max-lg:right-1 md:max-lg:top-1 md:max-lg:ml-0' : ''}`}
                        >
                          {badge.text}
                        </span>
                      ) : null}
                      {rail && !tipsDismissed && (
                        <span
                          data-nav-tooltip
                          aria-hidden="true"
                          className="absolute left-full top-1/2 z-[45] ml-2 hidden -translate-y-1/2 whitespace-nowrap rounded bg-ipc-primary px-2 py-1 text-xs text-white md:max-lg:group-hover:block md:max-lg:group-focus-visible:block"
                        >
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
